import { useActionState } from 'react'
import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorMode,
  useToast,
} from '@chakra-ui/react'
import { Check } from 'lucide-react'

interface WaitlistModalProps {
  isOpen: boolean
  onClose: () => void
  planName: string
}

type FormState = { submitted: boolean; error: string | null }

export function WaitlistModal({ isOpen, onClose, planName }: WaitlistModalProps) {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'
  const toast = useToast()

  const [state, formAction, isPending] = useActionState(
    async (_prevState: FormState, formData: FormData): Promise<FormState> => {
      const email = formData.get('email') as string

      if (!email) {
        toast({
          title: 'Please enter your email',
          status: 'error',
          duration: 3000,
        })
        return { submitted: false, error: 'Email required' }
      }

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/waitlist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, plan: planName.toLowerCase() }),
        })

        if (!response.ok) {
          throw new Error('Failed to join waitlist')
        }

        return { submitted: true, error: null }
      } catch {
        toast({
          title: 'Something went wrong',
          description: 'Please try again later.',
          status: 'error',
          duration: 5000,
        })
        return { submitted: false, error: 'Failed to join' }
      }
    },
    { submitted: false, error: null }
  )

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg={isDark ? 'stone.800' : 'white'}>
        <ModalHeader>Join the waitlist</ModalHeader>
        <ModalCloseButton />
        <form action={formAction}>
          <ModalBody>
            {state.submitted ? (
              <VStack spacing={3} py={4}>
                <Icon as={Check} boxSize={8} color="green.500" />
                <Text fontWeight="medium">You're on the list!</Text>
                <Text color="stone.500" fontSize="sm" textAlign="center">
                  We'll email you when the {planName} plan is available.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Text color="stone.500" fontSize="sm">
                  Enter your email and we'll notify you when the {planName} plan is ready.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    isDisabled={isPending}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {state.submitted ? (
              <Button variant="ghost" onClick={handleClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  isLoading={isPending}
                  loadingText="Joining..."
                >
                  Join waitlist
                </Button>
              </>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
